import { useState } from "react";
import { Textarea } from "../../components/ui/textarea";
import { CustomDatePicker } from "../custom-date-picker";
import { SlotPicker } from "./slot-picker";
import { Upload, X, Tag } from "lucide-react";
import { Slot, SlotsStatus } from "../../types/booking";
import { formatSlotTime } from "../../lib/timezone";
import { cn } from "../../lib/util";
import { fieldControlClass } from "./field";

interface ScheduleStepProps {
  formData: any;
  selectedDate: string;
  slots: Slot[];
  slotsStatus: SlotsStatus;
  selectedSlot: Slot | null;
  onDateSelect: (date: string) => void;
  onSlotSelect: (slot: Slot) => void;
  onRetrySlots: () => void;
  onFieldChange: (field: string, value: any) => void;
  errors: Record<string, boolean>;
  featuredImageConsent: boolean | null;
  onConsentChange: (consent: boolean) => void;
  onImageUpload: (file: File) => void;
  onImageRemove: () => void;
  image: File | null;
}

const MAX_IMAGE_MB = 3;
const MAX_IMAGE_BYTES = MAX_IMAGE_MB * 1024 * 1024;

const consentPillClass =
  "flex min-h-[44px] cursor-pointer items-center justify-center rounded-full border " +
  "border-[#E3C9C8] bg-white px-5 text-sm text-[#3a2e2c] transition " +
  "peer-checked:border-[#B87775] peer-checked:bg-[#B87775] peer-checked:text-white " +
  "peer-focus-visible:ring-2 peer-focus-visible:ring-[#B87775] peer-focus-visible:ring-offset-2 " +
  "hover:border-[#B87775]";

export function ScheduleStep({
  formData,
  selectedDate,
  slots,
  slotsStatus,
  selectedSlot,
  onDateSelect,
  onSlotSelect,
  onRetrySlots,
  onFieldChange,
  errors,
  featuredImageConsent,
  onConsentChange,
  onImageUpload,
  onImageRemove,
  image,
}: ScheduleStepProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const handleFiles = (files: FileList | null) => {
    const file = files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setUploadError("Please choose an image file (JPG or PNG).");
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      setUploadError(`That image is too large — please keep it under ${MAX_IMAGE_MB} MB.`);
      return;
    }
    setUploadError(null);
    onImageUpload(file);
  };

  return (
    <div className="space-y-10">
      {/* ── When ─────────────────────────────────────────────── */}
      <fieldset className="space-y-6">
        <legend className="mb-1 text-xs uppercase tracking-[0.2em] text-[#B87775]">
          Choose your time
        </legend>

        <div>
          <p className="font-serif text-[17px] leading-snug text-[#3a2e2c]">
            Pick a date
            <span className="text-[#B87775]" aria-hidden="true">
              {" "}
              *
            </span>
          </p>
          <div className="mt-3">
            {/* Date picker is owned elsewhere — we only frame it. */}
            <CustomDatePicker
              selectedDate={selectedDate}
              onDateSelect={onDateSelect}
              error={errors.date}
            />
          </div>
          <div aria-live="polite">
            {errors.date && (
              <p className="mt-2 text-sm text-[#c0504d]">Please select an available date.</p>
            )}
          </div>
        </div>

        <div>
          <div className="flex flex-wrap items-baseline justify-between gap-x-3">
            <span className="font-serif text-[17px] leading-snug text-[#3a2e2c]">
              Pick a time
              <span className="text-[#B87775]" aria-hidden="true">
                {" "}
                *
              </span>
            </span>
            <span className="text-[13px] text-[#9A6B69]">All times in Atlantic Time</span>
          </div>
          <div className="mt-3">
            {/* Slot picker is the existing, finished component — left as-is. */}
            <SlotPicker
              slots={slots}
              selectedSlot={selectedSlot}
              status={slotsStatus}
              hasDate={!!selectedDate}
              onSelect={onSlotSelect}
              onRetry={onRetrySlots}
              error={errors.time}
            />
          </div>
          {selectedSlot && (
            <p
              className="mt-3 inline-flex items-center gap-2 rounded-full bg-[#FBEFEF] px-4 py-2 text-sm text-[#8a5a58]"
              aria-live="polite"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-[#B87775]" aria-hidden="true" />
              <span>
                Selected{" "}
                <span className="font-semibold tabular-nums">
                  {formatSlotTime(selectedSlot.start)}
                </span>{" "}
                – {formatSlotTime(selectedSlot.end)}
              </span>
            </p>
          )}
          <div aria-live="polite">
            {errors.time && !selectedSlot && (
              <p className="mt-2 text-sm text-[#c0504d]">Please choose an available time.</p>
            )}
          </div>
        </div>
      </fieldset>

      <div className="h-px bg-[#EAD7D6]" />

      {/* ── A little more ────────────────────────────────────── */}
      <fieldset className="space-y-6">
        <legend className="mb-1 text-xs uppercase tracking-[0.2em] text-[#B87775]">
          A little more
        </legend>

        {/* Social-share discount opt-in */}
        <div className="rounded-xl border border-[#EAD7D6] bg-[#FBEFEF]/40 p-4">
          <div className="flex items-start gap-3">
            <Tag className="mt-0.5 h-5 w-5 shrink-0 text-[#B87775]" aria-hidden="true" />
            <div>
              <p className="font-serif text-[17px] text-[#3a2e2c]">
                Share your studio photos & save 10%
              </p>
              <p className="mt-1 text-sm leading-relaxed text-[#7c6c6a]">
                Happy for us to feature your look on social media? It's entirely up to you —
                and there's a 10% thank-you if you do.
              </p>
            </div>
          </div>
          <fieldset className="mt-4">
            <legend className="sr-only">
              Consent to feature your photos for a 10% discount
            </legend>
            <div className="flex gap-3">
              <label htmlFor="consent-yes" className="relative">
                <input
                  type="radio"
                  id="consent-yes"
                  name="photoConsent"
                  checked={featuredImageConsent === true}
                  onChange={() => onConsentChange(true)}
                  className="peer sr-only"
                />
                <span className={consentPillClass}>Yes — save 10%</span>
              </label>
              <label htmlFor="consent-no" className="relative">
                <input
                  type="radio"
                  id="consent-no"
                  name="photoConsent"
                  checked={featuredImageConsent === false}
                  onChange={() => onConsentChange(false)}
                  className="peer sr-only"
                />
                <span className={consentPillClass}>No, thank you</span>
              </label>
            </div>
          </fieldset>
        </div>

        {/* Details */}
        <div className="space-y-2">
          <label
            htmlFor="details"
            className="block font-serif text-[17px] leading-snug text-[#3a2e2c]"
          >
            Tell us about the look you're after
            <span className="ml-1.5 align-middle text-sm italic text-[#9A6B69]/80">
              optional
            </span>
          </label>
          <p className="text-sm leading-relaxed text-[#8a7a78]">
            Skin tone, any allergies, the occasion, your outfit colour and theme — the more you
            share, the closer we get to your vision.
          </p>
          <Textarea
            id="details"
            placeholder="On the day I'm wearing emerald green for a friend's wedding…"
            value={formData.details ?? ""}
            onChange={(e) => onFieldChange("details", e.target.value)}
            className={cn(fieldControlClass, "min-h-[120px] resize-y")}
          />
        </div>

        {/* Photo upload */}
        <div className="space-y-2">
          <p className="font-serif text-[17px] leading-snug text-[#3a2e2c]">
            Share a natural photo of yourself
            <span className="ml-1.5 align-middle text-sm italic text-[#9A6B69]/80">
              optional
            </span>
          </p>
          <p className="text-sm leading-relaxed text-[#8a7a78]">
            By uploading, you consent to us using your photo to prepare for your session. See
            our{" "}
            <a
              href="/privacy-policy"
              className="font-medium text-[#B87775] underline underline-offset-2 hover:text-[#a96764]"
            >
              Privacy Policy
            </a>
            .
          </p>

          {image ? (
            <div className="flex items-center gap-4 rounded-xl border border-[#EAD7D6] bg-white p-4">
              <img
                src={URL.createObjectURL(image)}
                alt="Your uploaded preview"
                className="h-20 w-20 rounded-lg object-cover"
              />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm text-[#3a2e2c]">{image.name}</p>
                <p className="text-xs text-[#9A6B69]">
                  {(image.size / 1024 / 1024).toFixed(1)} MB
                </p>
              </div>
              <button
                type="button"
                onClick={onImageRemove}
                aria-label="Remove uploaded photo"
                className="flex h-9 w-9 items-center justify-center rounded-full text-[#9A6B69] transition hover:bg-[#FBEFEF] hover:text-[#B87775] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B87775]"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          ) : (
            <div
              onDragOver={(e) => {
                e.preventDefault();
                setIsDragging(true);
              }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={(e) => {
                e.preventDefault();
                setIsDragging(false);
                handleFiles(e.dataTransfer.files);
              }}
              className={cn(
                "flex flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed px-4 py-10 text-center transition-colors",
                isDragging ? "border-[#B87775] bg-[#FBEFEF]" : "border-[#E3C9C8] bg-white"
              )}
            >
              <div className="rounded-full bg-[#FBEFEF] p-3">
                <Upload className="h-6 w-6 text-[#B87775]" aria-hidden="true" />
              </div>
              <p className="text-sm text-[#7c6c6a]">Drag a photo here, or</p>
              <label
                htmlFor="photo-upload"
                className="cursor-pointer rounded-full border border-[#E3C9C8] px-5 py-2.5 text-sm font-medium text-[#B87775] transition hover:border-[#B87775] hover:bg-[#FBEFEF] focus-within:ring-2 focus-within:ring-[#B87775] focus-within:ring-offset-2"
              >
                Browse files
                <input
                  type="file"
                  id="photo-upload"
                  accept="image/*"
                  className="sr-only"
                  onChange={(e) => handleFiles(e.target.files)}
                />
              </label>
              <p className="text-xs text-[#9A6B69]">JPG or PNG · up to {MAX_IMAGE_MB} MB</p>
            </div>
          )}

          <div aria-live="polite">
            {uploadError && (
              <p className="flex items-start gap-1.5 text-sm text-[#c0504d]">
                <span aria-hidden="true" className="mt-px select-none">
                  ✕
                </span>
                <span>{uploadError}</span>
              </p>
            )}
          </div>
        </div>
      </fieldset>
    </div>
  );
}
